import { localDayISO } from './localDay.js'

// Product threshold proposed by FOS-PRIMARY-WEAR-MODEL-001: two hours filters
// brief context swaps (including a typical short Gym wear) while admitting any
// sustained wear. The aggregation applies it to every tag identically.
export const MOST_WORN_MINIMUM_SECONDS = 2 * 60 * 60

export function localDayEndISO(day) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(day || ''))) return ''
  const start = new Date(`${day}T00:00:00`)
  if (Number.isNaN(start.getTime()) || localDayISO(start) !== day) return ''
  start.setDate(start.getDate() + 1)
  return start.toISOString()
}

export function primaryEventForWear(wear, application, eventId = crypto.randomUUID()) {
  const selectedAt = application?.applied_at
  const dayEndsAt = localDayEndISO(wear?.worn_on)
  if (!wear?.worn_on || !Number.isFinite(Date.parse(selectedAt)) || !dayEndsAt) {
    throw new Error('A dated initial application is required for the primary-wear timeline.')
  }
  return { id: eventId, selectedAt, dayEndsAt }
}

export async function recordPrimaryWearSelection(client, request) {
  // Pending wear requests created by the flag-off build have no primaryEvent.
  // Derive the same immutable values without rewriting that saved request.
  const event = request?.primaryEvent || primaryEventForWear(request?.wear, request?.application, request?.requestId)
  if (!event?.id || !event.selectedAt || !event.dayEndsAt) {
    throw new Error('The primary-wear selection request is incomplete.')
  }
  let response
  try {
    response = await client.rpc('record_primary_wear_event', {
      p_event_id: event.id,
      p_wear_id: request.requestId,
      p_selected_at: event.selectedAt,
      p_day_ends_at: event.dayEndsAt,
    })
  } catch (error) {
    error.primarySelectionPending = true
    throw error
  }
  if (response.error) {
    response.error.primarySelectionPending = true
    throw response.error
  }
  const value = response.data
  if (value?.event?.id !== event.id
      || value.event.wear_log_id !== request.requestId
      || value.event.user_id !== request.ownerId
      || value?.primary?.user_id !== request.ownerId) {
    const error = new Error('Primary-wear confirmation is incomplete. Retry the original wear save.')
    error.primarySelectionPending = true
    throw error
  }
  return value
}

function milliseconds(value) {
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : null
}

export function primaryWearIntervals(events = [], now = new Date()) {
  const nowMs = now instanceof Date ? now.getTime() : milliseconds(now)
  const ordered = events
    .map(event => ({ event, selectedAt: milliseconds(event.selected_at), dayEndsAt: milliseconds(event.day_ends_at) }))
    .filter(item => item.selectedAt !== null && item.dayEndsAt !== null && item.dayEndsAt > item.selectedAt)
    .sort((left, right) => left.selectedAt - right.selectedAt || String(left.event.id).localeCompare(String(right.event.id)))

  return ordered.map((item, index) => {
    const nextAt = ordered[index + 1]?.selectedAt ?? Number.POSITIVE_INFINITY
    const endAt = Math.min(nextAt, item.dayEndsAt, Number.isFinite(nowMs) ? nowMs : item.dayEndsAt)
    return {
      ...item.event,
      ended_at: new Date(Math.max(item.selectedAt, endAt)).toISOString(),
      duration_seconds: Math.max(0, (endAt - item.selectedAt) / 1000),
    }
  })
}

export function rankMostWornByDuration(events = [], wears = [], {
  minimumSeconds = MOST_WORN_MINIMUM_SECONDS,
  now = new Date(),
} = {}) {
  const wearsById = new Map(wears.map(wear => [wear.id, wear]))
  const totals = new Map()
  for (const interval of primaryWearIntervals(events, now)) {
    if (interval.duration_seconds < minimumSeconds) continue
    const wear = wearsById.get(interval.wear_log_id)
    if (!wear?.fragrance_id) continue
    const current = totals.get(wear.fragrance_id) || {
      fragrance_id: wear.fragrance_id,
      qualified_wear_count: 0,
      total_duration_seconds: 0,
    }
    current.qualified_wear_count += 1
    current.total_duration_seconds += interval.duration_seconds
    totals.set(wear.fragrance_id, current)
  }
  return [...totals.values()].sort((left, right) =>
    right.total_duration_seconds - left.total_duration_seconds
      || right.qualified_wear_count - left.qualified_wear_count
      || left.fragrance_id.localeCompare(right.fragrance_id))
}

export function projectPrimaryWearLog(logs = [], primaryWearId = '') {
  const primary = logs.find(wear => wear.id === primaryWearId) || null
  return {
    primary,
    todaysLog: logs.filter(wear => wear.id !== primaryWearId),
  }
}
